import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Table, Form } from 'react-bootstrap';
import { useTranslation } from '../../i18n';
import API_BASE_URL from '../../config/api';

// ✅ Ahora busca sus propios datos (igual que ElevationChart.js), así puede
// vivir junto a "Planificado vs Completado" sin depender de ActivitySummary.js.
const ActivityMetricsTable = ({ fileId }) => {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState(null);
  const [vo2max, setVo2max] = useState(null);

  useEffect(() => {
    if (!fileId) return;
    let alive = true;

    const fetchData = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/fit/parsed-data/${fileId}`);
        const data = res.data;
        if (!alive || !data?.laps?.length) return;

        const laps = data.laps;
        const avgSpeeds = laps.map(l => (l.enhanced_avg_speed || 0) * 3.6).filter(v => v > 0);
        const maxSpeeds = laps.map(l => (l.enhanced_max_speed || 0) * 3.6).filter(v => v > 0);
        const avgHRs = laps.map(l => l.avg_heart_rate).filter(Boolean);
        const maxHRs = laps.map(l => l.max_heart_rate).filter(Boolean);
        const avgCads = laps.map(l => l.avg_cadence).filter(v => v != null);
        const maxCads = laps.map(l => l.max_cadence).filter(v => v != null);

        const avgOf = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
        const maxOf = (arr) => arr.length ? Math.max(...arr) : null;

        const speedToPace = (kph) => {
          if (!kph) return 'N/A';
          const minPerKm = 60 / kph;
          const m = Math.floor(minPerKm);
          const s = Math.round((minPerKm - m) * 60);
          return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        };

        const avgSpeedVal = avgOf(avgSpeeds);
        const maxSpeedVal = maxOf(maxSpeeds);

        const maxCadFallback = maxOf(maxCads) || data.session?.max_cadence || null;
        const maxHRFallback  = maxOf(maxHRs)  || data.session?.max_heart_rate || null;
        const maxSpeedFallback = maxSpeedVal || 
          ((data.session?.enhanced_max_speed || data.session?.max_speed || 0) * 3.6) || null;
        setMetrics({
          ritmo_medio: { avg: speedToPace(avgSpeedVal), max: speedToPace(maxSpeedFallback) },
          ritmo_cardiaco: { avg: Math.round(avgOf(avgHRs)) || 'N/A', max: maxHRFallback || 'N/A' },
          cadencia: { avg: Math.round(avgOf(avgCads)) || 'N/A', max: maxCadFallback || 'N/A' },
          velocidad: { avg: avgSpeedVal ? avgSpeedVal.toFixed(2) : 'N/A', max: maxSpeedVal ? maxSpeedVal.toFixed(2) : 'N/A' },
        });

        const distanceKm = (data.session?.total_distance || 0) / 1000;
        const totalTimerTime = data.session?.total_timer_time || 0;
        if (distanceKm > 0 && totalTimerTime > 0) {
          const speedMs = (distanceKm * 1000) / totalTimerTime;
          setVo2max(Math.round((speedMs * 60 * 0.2 + 3.5) * 100) / 100);
        }
      } catch (err) {
        console.error('❌ Error al obtener métricas adicionales:', err.message);
      }
    };

    fetchData();
    return () => { alive = false; };
  }, [fileId]);

  if (!metrics) return null;

  const metricFields = [
    { key: 'ritmo_medio', label: t('activityDetails.avgPaceMetric'), unit: 'min/km' },
    { key: 'ritmo_cardiaco', label: t('activityDetails.heartRateMetric'), unit: 'bpm' },
    { key: 'cadencia', label: t('activityDetails.cadenceMetric'), unit: 'rpm' },
    { key: 'velocidad', label: t('activityDetails.speedMetric'), unit: 'kph' },
  ];

  return (
    <div className="metrics-table-container">
      <h5 style={{ fontSize: 14, fontWeight: 700, color: '#193255', margin: '8px 0 5px' }}>
        {t('activityDetails.additionalMetrics')}
      </h5>
      <Table striped bordered hover style={{ fontSize: 11 }}>
        <thead style={{ backgroundColor: '#193255', color: '#fff' }}>
          <tr>
            <th>{t('activityDetails.metricLabel')}</th>
            <th>{t('activityDetails.avgLabel')}</th>
            <th>{t('activityDetails.maxLabel')}</th>
            <th>{t('activityDetails.unitLabel')}</th>
          </tr>
        </thead>
        <tbody>
          {metricFields.map(({ key, label, unit }) => (
            <tr key={key}>
              <td>{label}</td>
              <td>{metrics[key].avg}</td>
              <td>{metrics[key].max}</td>
              <td>{unit}</td>
            </tr>
          ))}
          {vo2max != null && (
            <tr>
              <td>{t('activityDetails.vo2maxMetric')}</td>
              <td>{vo2max}</td>
              <td>-</td>
              <td>ml/kg/min</td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default ActivityMetricsTable;
